<template>
  <div class="flex flex-col items-center job-list_wrapper">
    <div class="flex items-center justify-center w-11/12 md:w-5/6 mb-12 max-w-4xl">
      <form class="flex flex-col sm:flex-row items-center w-full bg-white rounded" @submit.prevent="onSubmit">
        <div class="w-full border-r-solid p-1 border-lightgray"
             :class="[widgetConfig.SHOW_LOCATION_SEARCH || widgetConfig.SHOW_RANGE_SELECT ? 'sm:border-r' : 'border-0']">
          <div class="flex flex-row items-center mx-2">
            <search-icon size="1.5x" class="mr-2 custom-icon-class"></search-icon>
            <el-input v-model="position"
                      placeholder="What job are you looking for?"
                      :trigger-on-focus=false
                      @select="handleSuggestionSelect"
                      value-key="keyword"
                      class="autocomplete-input">
            </el-input>
            <x-icon v-show="position" size="1x" class="cursor-pointer custom-icon-class"
                    @click="clearPosition"></x-icon>
          </div>
        </div>
        <div v-if="widgetConfig.SHOW_LOCATION_SEARCH"
             class="w-full flex flex-row items-center p-1 w-56 border-r-solid h-12 border-lightgray"
             :class="[widgetConfig.SHOW_RANGE_SELECT ? 'sm:border-r' : 'border-0']">
          <div class="flex flex-row items-center w-full mx-2">
            <map-pin-icon size="1.5x" class="mr-2 custom-icon-class"></map-pin-icon>
            <vue-google-autocomplete id="location-autocomplete"
                                     class="w-full text-black"
                                     :country="autocompleteCountries"
                                     autocomplete="no"
                                     placeholder="Location"
                                     @placechanged="onCityChange"
                                     ref="googleAutocomplete"
                                     types="(cities)">
            </vue-google-autocomplete>
            <x-icon size="1x" class="cursor-pointer ml-2 custom-icon-class" v-show="location_name"
                    @click="clearLocation"></x-icon>
          </div>
        </div>
        <div v-if="widgetConfig.SHOW_RANGE_SELECT" class="w-full max-w-xxs flex items-center">
          <el-select v-model="range" class="w-full range-select">
            <el-option v-for="(item, key) in rangeOptions"
                       :key="key"
                       :label="item.label"
                       :value="item.value"/>
          </el-select>
        </div>
        <el-button class="p-3 mb-1 mx-1 w-full sm:w-auto sm:mb-0" native-type="submit">
          <search-icon size="1.3x" class="text-bold"></search-icon>
        </el-button>
      </form>
    </div>
    <div class="flex flex-row justify-center md:justify-around md:min-w-7xl w-full">
      <div class="mr-3 md:w-2/6">
        <JobListFilters :jobSegments="selectedSegments" @change="onSegmentsChange" @loading="load(true)"
                        @loadingFinished="load(false)"/>
      </div>
      <div class="flex flex-col items-center justify-start md:w-3/6">
        <div class="self-start w-full">
          <FiltersPreview class="filters-preview not-visible md:block" :segments="selectedSegments"
                          @change="onSegmentsChange"/>
        </div>
        <hr v-if="selectedSegments.length > 0" class="border-t-1 border-t-solid border-t-gray w-full h-2 mt-4 -mb-2"/>
        <div class="flex items-center text-xl font-semibold h-20">
          <div v-if="loading" class="loader"></div>
          <div v-else>{{jobs.total}}
            <span v-if="jobs.total === 1">{{$tc('jobList.foundJob', 1)}}</span>
            <span v-else>{{$tc('jobList.foundJob', 2)}}</span>
          </div>
        </div>
        <div class="w-full mb-4 max-w-2xl" v-for="job in jobs.data" :key="job.uuid">
          <JobItem :job="job"/>
        </div>
        <el-pagination :current-page="jobs.currentPage"
                       :page-size="jobsPerPage"
                       :pager-count="5"
                       @current-change="pageChange"
                       background
                       layout="pager"
                       :total="jobs.total">
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
  import JobItem from '../components/JobItem.vue'
  import JobListFilters from '../components/filters/JobListFilters'
  import listParamsMixin from '../util/mixins/listParamsMixin'
  import queryParamsMixin from '../util/mixins/queryParamsMixin'
  import FiltersPreview from '../components/filters/FiltersPreview'
  import VueGoogleAutocomplete from '../components/search-job/GoogleAutocomplete'
  import { loadGoogleMaps } from '../API/googleMapsLoader'
  import { MapPinIcon, SearchIcon, XIcon } from 'vue-feather-icons'
  import { RANGE_OPTIONS } from '../util/rangeOptions'
  import { TRACKING_EVENTS } from '../enum/tracking'

  export default {
    name: 'job-list',
    components: {
      FiltersPreview,
      JobItem,
      JobListFilters,
      VueGoogleAutocomplete,
      MapPinIcon,
      SearchIcon,
      XIcon
    },
    mixins: [listParamsMixin, queryParamsMixin],
    data () {
      return {
        lat: '',
        lon: '',
        location_name: '',
        position: '',
        range: 10,
        rangeOptions: RANGE_OPTIONS,
        widgetConfig: {},
        loading: false
      }
    },
    watch: {
      jobs (newValue) {
        this.$analytics(TRACKING_EVENTS.JOB_SEARCH, {
          eventLabel: JSON.stringify(this.$route.query),
          eventValue: newValue.total
        })
      }
    },
    async created () {
      this.widgetConfig = this.$store.getters['widgetConfig']
      const mapsApiKey = this.widgetConfig.MAPS_KEY
      loadGoogleMaps(mapsApiKey)
    },
    async mounted () {
      if (!this.widgetConfig.SHOW_LOCATION_SEARCH) {
        return
      }
      this.$refs.googleAutocomplete.update(this.$route.query['location_name'])
      this.$analytics(TRACKING_EVENTS.JOB_SEARCH, {
        eventLabel: JSON.stringify(this.$route.query),
        eventValue: this.jobs.total
      })
    },
    computed: {
      jobs () {
        return this.$store.state.allJobs
      },
      segments () {
        return this.$store.state.allJobs.aggregations
      },
      autocompleteCountries () {
        let countries = this.$store.getters['countries']
        if (countries.length < 1) {
          return
        }
        return countries
      }
    },
    methods: {
      load (value) {
        this.loading = value
      },
      onCityChange (place) {
        this.location_name = place.locality
        this.lat = place.latitude
        this.lon = place.longitude
      },
      onSubmit (event, extraQuery = {}) {
        const query = {
          ...this.$route.query,
          'search': this.position || undefined,
          'distance': this.range || undefined,
          'location_name': this.location_name || undefined,
          'lat': this.lat || undefined,
          'lon': this.lon || undefined,
          'page': 1,
          ...extraQuery
        }
        this.$router.push({ query: query })
      },
      clearLocation () {
        this.$refs.googleAutocomplete.clear()
        this.onCityChange({})
      },
      clearPosition () {
        this.position = ''
      },
      handleSuggestionSelect (item) {
        const query = {
          'search': item.keyword,
          ...this.getSegmentsQuery(item.query.segments)
        }
        this.onSubmit(null, query)
      }
    }
  }
</script>

